// src/poker/hooks/usePokerClock.js
import { useCallback, useEffect, useRef } from "react";
import { buildDefaultLevels } from "../pokerClock.logic";

export default function usePokerClock({
  loadedOnce,
  levels,
  setLevels,
  levelIndex,
  setLevelIndex,
  running,
  setRunning,
  remainingSec,
  setRemainingSec,
}) {
  const tickRef = useRef(null);

  // jeśli levele się zmieniły, a remainingSec jest puste/<=0, ustaw na długość aktualnego levelu
  useEffect(() => {
    if (!loadedOnce) return;
    const lvl = levels?.[levelIndex];
    if (!lvl) return;

    if (!Number.isFinite(remainingSec) || remainingSec <= 0) {
      setRemainingSec(lvl.minutes * 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedOnce, levels, levelIndex]);

  const selectLevel = useCallback(
    (i) => {
      const lvl = levels?.[i];
      if (!lvl) return;
      setRunning(false);
      setLevelIndex(i);
      setRemainingSec(lvl.minutes * 60);
    },
    [levels, setLevelIndex, setRemainingSec, setRunning]
  );

  const goPrev = useCallback(() => {
    setRunning(false);
    setLevelIndex((idx) => {
      const n = Math.max(0, idx - 1);
      const lvl = levels?.[n];
      if (lvl) setRemainingSec(lvl.minutes * 60);
      return n;
    });
  }, [levels, setLevelIndex, setRemainingSec, setRunning]);

  const goNext = useCallback(() => {
    setRunning(false);
    setLevelIndex((idx) => {
      const n = Math.min((levels?.length || 1) - 1, idx + 1);
      const lvl = levels?.[n];
      if (lvl) setRemainingSec(lvl.minutes * 60);
      return n;
    });
  }, [levels, setLevelIndex, setRemainingSec, setRunning]);

  const resetLevel = useCallback(() => {
    setRunning(false);
    const lvl = levels?.[levelIndex];
    if (!lvl) return;
    setRemainingSec(lvl.minutes * 60);
  }, [levels, levelIndex, setRemainingSec, setRunning]);

  const applyPreset = useCallback(
    (minutes) => {
      setRunning(false);
      const newLvls = buildDefaultLevels(minutes);
      setLevels(newLvls);
      setLevelIndex(0);
      setRemainingSec(newLvls[0].minutes * 60);
    },
    [setLevels, setLevelIndex, setRemainingSec, setRunning]
  );

  // Timer interval
  useEffect(() => {
    if (!running) {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      return;
    }

    tickRef.current = setInterval(() => {
      setRemainingSec((prev) => {
        if (prev <= 1) {
          setRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [running, setRemainingSec, setRunning]);

  // Hotkeys: Space start/pause, ← prev, → next
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;

      if (e.code === "Space") {
        e.preventDefault();
        setRunning((r) => !r);
      }
      if (e.code === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
      if (e.code === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, setRunning]);

  return {
    selectLevel,
    goPrev,
    goNext,
    resetLevel,
    applyPreset,
  };
}
